// Invitation service
// - Utilities for creating, retrieving, and managing household invitations
// - Uses Supabase for database operations and edge function integrations

import { supabase } from "../lib/supabase";

// Maximum number of members allowed in a single household (1 admin + 5 others).
// Must be kept in sync with MAX_HOUSEHOLD_MEMBERS in:
//   - supabase/functions/send-invitation-email/index.ts
//   - src/components/HouseholdSettings.tsx
export const MAX_HOUSEHOLD_MEMBERS = 6;

export interface HouseholdInvitation {
  id: string;
  household_id: string;
  email: string;
  token: string;
  invited_by: string;
  status: "pending" | "accepted" | "expired" | "revoked";
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
  household_name?: string;
  inviter_email?: string;
}

// Create an invitation and send email via Supabase edge function
// Returns the created invitation or a user-friendly error message
export async function createInvitation(
  householdId: string,
  email: string,
  invitedBy: string,
): Promise<{
  invitation: HouseholdInvitation | null;
  error?: string;
}> {
  try {
    // Call the edge function to create invitation and send email
    const { data: functionData, error: functionError } =
      await supabase.functions.invoke("send-invitation-email", {
        body: {
          householdId: parseInt(householdId, 10),
          email: email.toLowerCase().trim(),
          invitedBy: invitedBy,
        },
      });

    if (functionError) {
      console.error("🔴 Error calling edge function:", functionError);

      // Extract user-friendly error message
      let userMessage = "Unable to send invitation. Please try again later.";

      if (functionError.message) {
        if (functionError.message.includes("not found")) {
          userMessage = "Household not found. Please contact support.";
        } else if (!functionError.message.includes("Edge Function returned")) {
          userMessage = functionError.message;
        }
      }

      return {
        invitation: null,
        error: userMessage,
      };
    }

    if (functionData?.success && functionData?.invitation) {
      return {
        invitation: functionData.invitation,
      };
    }

    return {
      invitation: null,
      error:
        functionData?.error || "Unable to send invitation. Please try again.",
    };
  } catch (error) {
    console.error("🔴 Error in createInvitation:", error);
    return {
      invitation: null,
      error: "An unexpected error occurred. Please try again.",
    };
  }
}

// Fetch invitations for a household ordered by creation time
export async function getHouseholdInvitations(
  householdId: string,
): Promise<HouseholdInvitation[]> {
  const { data, error } = await supabase
    .from("household_invitations")
    .select(
      "id, household_id, email, token, invited_by, status, expires_at, created_at, accepted_at",
    )
    .eq("household_id", householdId)
    .eq("status", "pending")
    .gt("expires_at", new Date().toISOString())
    .order("created_at", { ascending: false });

  if (error) {
    console.error("🔴 Error fetching invitations:", error);
    return [];
  }

  return data || [];
}

// Fetch a single invitation by token and include household name when available
export async function getInvitationByToken(
  token: string,
): Promise<HouseholdInvitation | null> {
  const { data, error } = await supabase
    .from("household_invitations")
    .select(
      `
      id, household_id, email, token, invited_by, status, expires_at, created_at, accepted_at,
      households (
        name
      )
    `,
    )
    .eq("token", token)
    .maybeSingle();

  if (error) {
    console.error("🔴 Error fetching invitation:", error);
    return null;
  }

  if (!data) {
    return null;
  }
  type Household = { name: string };
  const invitation = {
    ...data,
    household_name: Array.isArray(data.households)
      ? (data.households[0] as Household)?.name
      : (data.households as Household)?.name,
  };
  return invitation;
}

// Get active pending invitations for a specific email
export async function getPendingInvitationsForEmail(
  email: string,
): Promise<HouseholdInvitation[]> {
  const { data, error } = await supabase
    .from("household_invitations")
    .select(
      `
      id, household_id, email, token, invited_by, status, expires_at, created_at, accepted_at,
      households (
        name
      )
    `,
    )
    .eq("email", email.toLowerCase().trim())
    .eq("status", "pending")
    .gt("expires_at", new Date().toISOString())
    .order("created_at", { ascending: false });

  if (error) {
    console.error("🔴 Error fetching pending invitations:", error);
    return [];
  }

  return (data || []).map((invitation) => ({
    ...invitation,
    household_name: (
      invitation.households as unknown as { name: string } | null
    )?.name,
  }));
}

// Accept an invitation by token and add the user to the household
export async function acceptInvitation(
  token: string,
  userId: string,
): Promise<boolean> {
  const invitation = await getInvitationByToken(token);

  if (!invitation) {
    console.error("🔴 Invitation not found.");
    return false;
  }

  if (invitation.status !== "pending") {
    console.error("🔴Invitation is not pending.");
    return false;
  }

  if (new Date(invitation.expires_at) < new Date()) {
    await supabase
      .from("household_invitations")
      .update({ status: "expired" })
      .eq("id", invitation.id);
    console.error("🔴 Invitation has expired.");
    return false;
  }

  // Last-mile enforcement: reject if the household already has the maximum
  // number of members. This catches edge cases such as a user holding an
  // invitation token that was issued before the cap was introduced.
  const { count: memberCount, error: memberCountError } = await supabase
    .from("household_members")
    .select("*", { count: "exact", head: true })
    .eq("household_id", invitation.household_id);

  if (memberCountError) {
    console.error(
      "🔴 Error checking household member count:",
      memberCountError,
    );
    return false;
  }

  if ((memberCount ?? 0) >= MAX_HOUSEHOLD_MEMBERS) {
    console.error(
      `🔴 Household ${invitation.household_id} is at capacity (${MAX_HOUSEHOLD_MEMBERS} members).`,
    );
    return false;
  }

  const { error: memberError } = await supabase
    .from("household_members")
    .insert({
      household_id: invitation.household_id,
      user_id: userId,
      role: "member",
    });

  if (memberError) {
    // 23505 = unique_violation — the user is already a member of this household.
    // This is acceptable (e.g. they clicked the invite link twice). Continue so
    // the invitation record is still marked accepted.
    if (memberError.code !== "23505") {
      console.error("🔴 Error adding user to household:", memberError);
      return false;
    }
    console.warn(
      "⚠️ User is already a member of this household — skipping insert.",
    );
  }

  const { error: updateError } = await supabase
    .from("household_invitations")
    .update({
      status: "accepted",
      accepted_at: new Date().toISOString(),
    })
    .eq("id", invitation.id);

  if (updateError) {
    console.error("🔴 Error updating invitation:", updateError);
    return false;
  }
  return true;
}

// Revoke an invitation by updating its status
export async function revokeInvitation(invitationId: string): Promise<boolean> {
  const { error } = await supabase
    .from("household_invitations")
    .update({ status: "revoked" })
    .eq("id", invitationId);

  if (error) {
    console.error("🔴 Error revoking invitation:", error);
    return false;
  }

  return true;
}

// Resend invitation email using Supabase edge function
// Returns success flag and optional user-facing error message
export async function resendInvitation(invitationId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // Call the edge function to resend the email
    const { data, error: functionError } = await supabase.functions.invoke(
      "resend-invitation-email",
      {
        body: {
          invitationId: invitationId,
        },
      },
    );

    if (functionError) {
      console.error("🔴 Error calling edge function:", functionError);

      // Extract user-friendly error message from function error
      let userMessage =
        "Unable to resend invitation at this time. Please try again later.";

      // Check if the error response contains our custom error message (e.g., rate limit)
      if (functionError.message) {
        // If message contains "Please wait", it's our rate limit message
        if (functionError.message.includes("Please wait")) {
          userMessage = functionError.message;
        } else if (functionError.message.includes("not found")) {
          userMessage = "Invitation not found. It may have been deleted.";
        } else if (!functionError.message.includes("Edge Function returned")) {
          // Use the error message if it's not the generic edge function error
          userMessage = functionError.message;
        }
      }

      return {
        success: false,
        error: userMessage,
      };
    }

    if (!data?.success) {
      return {
        success: false,
        error: data?.error || "Unable to resend invitation. Please try again.",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("🔴 Error in resendInvitation:", error);
    return {
      success: false,
      error: "An unexpected error occurred. Please try again.",
    };
  }
}

// Build a client-facing link for an invitation token.
// On native (Capacitor) the WebView origin is a custom scheme like
// "pantry://localhost" which is not a usable URL for recipients, so we
// always use the known production origin instead.
export function getInvitationLink(token: string): string {
  const { Capacitor } = window as Window & {
    Capacitor?: { isNativePlatform?: () => boolean };
  };
  const origin = Capacitor?.isNativePlatform?.()
    ? "https://pantry.hillardworks.com"
    : window.location.origin;
  return `${origin}?invitation=${token}`;
}
